// src/components/proposal-formats/ProposalFormatCalculationsPanel.tsx

import { useCallback, useEffect, useMemo, useState } from "react";
import type { OpportunityFormatKey } from "@/types/opportunity";
import { listFormatCalcLines } from "@/lib/opportunity/formatCalcLineClient";
import { evaluateFormatCalcLines } from "@/lib/opportunity/formatCalcEval";
import { computeFullDeposit } from "@/lib/opportunity/formatDepositEval";
import { getFormatDepositRule } from "@/lib/opportunity/formatDepositRuleClient";
import { getFormatSettings } from "@/lib/opportunity/formatSettingsClient";
import { DEFAULT_BASE_AMOUNT_LABEL } from "@/lib/opportunity/formatSettingsTypes";
import { selectFormatDepositRule } from "@/lib/opportunity/formatDepositResolver";
import {
  FORMAT_CALC_PLACEMENT_LABELS,
  FORMAT_CALC_PRODUCT_TYPE_WILDCARD,
  type FormatCalcLine,
} from "@/lib/opportunity/formatCalcLineTypes";
import type { FormatDepositRule } from "@/lib/opportunity/formatDepositTypes";
import type { ProductTypeEntry } from "@/hooks/useOrgSettings";
import { SectionTabSlider } from "./SectionTabSlider";
import { FormatBaseAmountEditor } from "./FormatBaseAmountEditor";
import { FormatCalcLinesSection } from "./FormatCalcLinesSection";
import { FormatDepositRulesEditor } from "./FormatDepositRulesEditor";
import { FormatPaymentMilestonesSection } from "./FormatPaymentMilestonesSection";
import { ProposalFormatCollapsibleSection } from "./ProposalFormatCollapsibleSection";

const DEPOSIT_PREVIEW_SALE_TOTAL = 10_000;

const CALC_TABS = [
  { id: "base_amount" as const, label: "Base amount" },
  { id: "before_subtotal" as const, label: FORMAT_CALC_PLACEMENT_LABELS.before_subtotal },
  { id: "after_subtotal" as const, label: FORMAT_CALC_PLACEMENT_LABELS.after_subtotal },
  { id: "deposit_rules" as const, label: "Deposit rules" },
];

type CalcTabId = (typeof CALC_TABS)[number]["id"];

export type ProposalFormatCalculationsPanelProps = {
  formatKey: OpportunityFormatKey;
  productType: string;
  productTypes: ReadonlyArray<ProductTypeEntry>;
};

export function ProposalFormatCalculationsPanel(props: ProposalFormatCalculationsPanelProps) {
  const { formatKey, productType, productTypes } = props;
  const [lines, setLines] = useState<FormatCalcLine[]>([]);
  const [depositRules, setDepositRules] = useState<FormatDepositRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [baseAmountLabel, setBaseAmountLabel] = useState(DEFAULT_BASE_AMOUNT_LABEL);
  const [calcTab, setCalcTab] = useState<CalcTabId>("base_amount");

  const label =
    productType === FORMAT_CALC_PRODUCT_TYPE_WILDCARD
      ? "Generic"
      : (productTypes.find((p) => p.tag === productType)?.label ?? productType);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [linesR, depR, settingsR] = await Promise.all([
      listFormatCalcLines({ format_key: formatKey, product_type: productType }),
      getFormatDepositRule({ format_key: formatKey, product_type: productType }),
      getFormatSettings({ format_key: formatKey, product_type: productType }),
    ]);
    setLoading(false);
    if (!linesR.ok) {
      setLoadError(linesR.error);
      setLines([]);
      return;
    }
    setLines(linesR.data.lines);
    setDepositRules(depR.ok && depR.data.rule ? [depR.data.rule] : []);
    setBaseAmountLabel(
      settingsR.ok && settingsR.data.settings?.base_amount_label
        ? settingsR.data.settings.base_amount_label
        : DEFAULT_BASE_AMOUNT_LABEL,
    );
  }, [formatKey, productType]);

  useEffect(() => {
    void reload();
  }, [reload, reloadTick]);

  const calcPreview = useMemo(
    () => evaluateFormatCalcLines({ lines, baseSubtotal: DEPOSIT_PREVIEW_SALE_TOTAL }),
    [lines],
  );

  const depositRule = useMemo(
    () => selectFormatDepositRule({ rules: depositRules, productType }),
    [depositRules, productType],
  );

  const depositPreview = useMemo(
    () =>
      computeFullDeposit({
        rule: depositRule,
        saleTotal: calcPreview.totalBeforeTax,
        evaluatedLines: [...calcPreview.before, ...calcPreview.after],
      }),
    [calcPreview, depositRule],
  );

  const bump = () => setReloadTick((t) => t + 1);

  return (
    <div className="space-y-3 min-w-0">
      {loadError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40">
          {loadError}
        </div>
      ) : null}

      <ProposalFormatCollapsibleSection
        title="Calculation rules"
        description={`Line items, deposit calculation, and base amount for ${label}.`}
        defaultOpen
      >
        <SectionTabSlider
          options={CALC_TABS}
          value={calcTab}
          onChange={setCalcTab}
          ariaLabel="Calculation section"
        />
        {loading ? (
          <p className="text-sm text-gray-500">Loading calculation rules…</p>
        ) : calcTab === "base_amount" ? (
          <FormatBaseAmountEditor
            embedded
            formatKey={formatKey}
            productType={productType}
            reloadTick={reloadTick}
            onSaved={bump}
          />
        ) : calcTab === "deposit_rules" ? (
          <FormatDepositRulesEditor
            embedded
            formatKey={formatKey}
            productType={productType}
            depositPreview={depositPreview}
            reloadTick={reloadTick}
            onSaved={bump}
          />
        ) : (
          <FormatCalcLinesSection
            embedded
            placement={calcTab}
            lines={lines}
            formatKey={formatKey}
            productType={productType}
            onChanged={bump}
            percentBasis={
              calcTab === "before_subtotal"
                ? DEPOSIT_PREVIEW_SALE_TOTAL
                : calcPreview.adjustedSubtotal
            }
          />
        )}
      </ProposalFormatCollapsibleSection>

      {!loading ? (
        <FormatPaymentMilestonesSection
          formatKey={formatKey}
          productType={productType}
          calcLines={lines}
          baseAmountLabel={baseAmountLabel}
          depositPreview={depositPreview}
          reloadTick={reloadTick}
          onChanged={bump}
        />
      ) : null}
    </div>
  );
}
